import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { MutationError, toDialogError } from './PartnerFormDialog';
import type { PartnerRepository } from './repository';
import type { PartnerDetail, PartnerStatus } from './types';

export function PartnerStatusDialog({
  onClose,
  onSaved,
  open,
  partner,
  repository,
}: {
  readonly onClose: () => void;
  readonly onSaved: (partner: PartnerDetail) => void;
  readonly open: boolean;
  readonly partner: PartnerDetail;
  readonly repository: PartnerRepository;
}) {
  const { t } = useTranslation();
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<{
    readonly status: PartnerStatus;
    readonly reason: string;
  }>({ defaultValues: { status: partner.status, reason: '' } });
  const proposed = watch('status');
  useEffect(() => {
    if (open) {
      reset({ status: partner.status, reason: '' });
      setError(null);
    }
  }, [open, partner.status, reset]);
  const submit = handleSubmit(async (values) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.changeStatus(partner.id, {
        status: values.status,
        reason: values.reason.trim(),
      });
      onSaved(result.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('partners.errors.mutation')));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog
      aria-describedby="partner-status-impact"
      fullWidth
      onClose={() => !submitting.current && onClose()}
      open={open}
    >
      <DialogTitle>{t('partners.status.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="partner-status-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            <Typography id="partner-status-impact">
              {t('partners.status.impact', {
                code: partner.code,
                current: partner.status,
                next: proposed,
              })}
            </Typography>
            <Typography variant="body2">{t('partners.status.closedTerminal')}</Typography>
          </Alert>
          <TextField
            defaultValue={partner.status}
            label={t('partners.fields.proposedStatus')}
            select
            {...register('status')}
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('partners.fields.reason')}
            multiline
            {...register('reason', {
              required: t('partners.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('partners.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="warning"
          form="partner-status-form"
          loading={busy}
          type="submit"
          variant="contained"
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
